import React from 'react';

interface DashboardWidgetProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
  error?: string;
  onRefresh?: () => void;
  refreshing?: boolean;
  lastUpdated?: Date;
  refreshButtonBelow?: boolean; // New prop to control refresh button position
  refreshButtonBottom?: boolean; // New prop to control refresh button at bottom
  titleIcon?: React.ReactNode; // Optional icon to display next to title
}

export const DashboardWidget: React.FC<DashboardWidgetProps> = ({ 
  title, 
  children, 
  className = '', 
  loading = false,
  error,
  onRefresh,
  refreshing = false,
  lastUpdated,
  refreshButtonBelow = false,
  refreshButtonBottom = false,
  titleIcon
}) => {
  if (error) {
    return (
      <div className={`dashboard-widget error ${className}`}>
        <h3 className="widget-title">
          {title}
          {titleIcon}
        </h3>
        <div className="widget-content error-content">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`dashboard-widget loading ${className}`}>
        <h3 className="widget-title">
          {title}
          {titleIcon}
        </h3>
        <div className="widget-content loading-content">
          <div className="skeleton-loader">
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-widget ${className}`}>
      <div className="widget-header">
        <h3 className="widget-title">
          {title}
          {titleIcon}
        </h3>
        {onRefresh && !refreshButtonBelow && !refreshButtonBottom && (
          <div className="widget-controls">
            {lastUpdated && (
              <span className="last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button 
              onClick={onRefresh} 
              className="refresh-button"
              disabled={refreshing}
            >
              {refreshing ? (
                <>
                  <div className="spinner small"></div>
                  Refreshing...
                </>
              ) : (
                'Refresh'
              )}
            </button>
          </div>
        )}
      </div>
      {onRefresh && refreshButtonBelow && (
        <div className="widget-refresh-row">
          <button 
            onClick={onRefresh} 
            className="refresh-button"
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <div className="spinner small"></div>
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
      <div className="widget-content">
        {children}
      </div>
      {onRefresh && refreshButtonBottom && (
        <div className="widget-refresh-bottom">
          <button 
            onClick={onRefresh} 
            className="refresh-button"
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <div className="spinner small"></div>
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated:<br/>{lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
